/**
 * Instrument identity on the client side.
 *
 * `resolveCode` mirrors the alias table in `web_contract.py`: COMPOSITE and
 * IHSG are one instrument, so the route, the request and the cache key all
 * agree before anything is fetched. The frontend resolves locally too, so the
 * URL `/ihsg` renders the right title without a round trip.
 */

export const DEFAULT_CODE = "composite";
export const DEFAULT_DISPLAY_NAME = "IHSG / COMPOSITE";

const ALIASES: Readonly<Record<string, string>> = {
	composite: DEFAULT_CODE,
	ihsg: DEFAULT_CODE,
	"ihsg/composite": DEFAULT_CODE,
};

const INDEX_DISPLAY: Readonly<Record<string, string>> = {
	[DEFAULT_CODE]: DEFAULT_DISPLAY_NAME,
};

/** Canonical backend code for anything the user typed, clicked or put in the URL. */
export function resolveCode(input: string | null | undefined): string {
	const requested = (input ?? "").trim().toLowerCase().replace(/^\/+|\/+$/g, "");
	if (!requested) return DEFAULT_CODE;
	return ALIASES[requested] ?? requested;
}

/** Title for a code, before any payload has arrived. */
export function displayNameFor(code: string): string {
	const canonical = resolveCode(code);
	return INDEX_DISPLAY[canonical] ?? canonical.toUpperCase();
}

export function isIndex(code: string): boolean {
	return resolveCode(code) in INDEX_DISPLAY;
}

export interface InstrumentOption {
	code: string;
	name: string;
	sector: string;
	/**
	 * The source row's own `index`, carried through untouched.
	 *
	 * `/param/list/{category}` returns the database's ordering, which is the
	 * ranking the desk already knows. Renumbering it here — or dropping it and
	 * relying on array position — would silently invent a different order the
	 * first time a caller filters or re-sorts.
	 */
	index: number;
}

/** Source order: the list route's own `index`, ascending. Never array position. */
export function bySourceIndex(a: InstrumentOption, b: InstrumentOption): number {
	return a.index - b.index;
}

/**
 * Autocomplete over the instrument list.
 *
 * Code prefix beats name match beats anything else, because a trader typing
 * "BB" means BBCA/BBRI/BBNI before they mean "Bank Rakyat". Ties keep the
 * source order — the row's own index, not where it happened to land in the
 * array after a merge.
 */
export function searchInstruments(query: string, options: readonly InstrumentOption[], limit = 8): InstrumentOption[] {
	const needle = query.trim().toLowerCase();
	// No query: the source ranking is the answer, so show it in its own order.
	if (!needle) return [...options].sort(bySourceIndex).slice(0, limit);

	const scored: Array<{ option: InstrumentOption; rank: number; position: number }> = [];
	options.forEach((option) => {
		const position = option.index;
		const code = option.code.toLowerCase();
		const name = option.name.toLowerCase();
		const aliasHit = Object.keys(ALIASES).some((alias) => alias.startsWith(needle) && ALIASES[alias] === code);
		if (code.startsWith(needle) || aliasHit) scored.push({ option, rank: 0, position });
		else if (name.startsWith(needle)) scored.push({ option, rank: 1, position });
		else if (code.includes(needle) || name.includes(needle)) scored.push({ option, rank: 2, position });
	});

	return scored
		.sort((a, b) => a.rank - b.rank || a.position - b.position)
		.slice(0, limit)
		.map((entry) => entry.option);
}

/** Most-recent-first, de-duplicated, capped. Pure so the caller owns storage. */
export function pushRecent(recents: readonly string[], code: string, limit = 6): string[] {
	const canonical = resolveCode(code);
	return [canonical, ...recents.filter((entry) => resolveCode(entry) !== canonical)].slice(0, limit);
}
